import { useMemo } from 'react';

import { useDecisionMakerProfilePageData } from '#src/app/pages/decisionMakerProfile/pageContext';

export type Endorsement = {
	id: string;
	person: {
		id: string;
		displayName: string;
		profilePhoto: { photoUrl: string | null } | null;
	} | null;
	organization: {
		name: string;
		profilePhoto: { photoUrl: string | null } | null;
	} | null;
};

export type VoterGuidePick = {
	id: string;
	voterGuide: {
		author: {
			displayName: string;
			profilePhoto: { photoUrl: string | null } | null;
		};
	};
};

type Result = ModelHookResult<{
	voterGuidePicks: VoterGuidePick[] | null;
	endorsements: Endorsement[] | null;
	totalRecommendations: number;
	headingText: string;
}>;

export const useEndorsersList = (): Result => {
	const {
		data: { enrichedData },
	} = useDecisionMakerProfilePageData();

	const endorsements: Endorsement[] = useMemo(() => {
		if (!enrichedData?.endorsements || enrichedData.endorsements.length === 0) return [];
		return enrichedData.endorsements.map((endorsement: Endorsement) => ({
			id: endorsement.id,
			person: endorsement.person ?? null,
			organization: endorsement.organization ?? null,
		}));
	}, [enrichedData]);

	const voterGuidePicks: VoterGuidePick[] = useMemo(() => {
		if (!enrichedData?.voterGuidePicks || enrichedData.voterGuidePicks.length === 0) return [];
		return enrichedData.voterGuidePicks.map((voterGuidePick: VoterGuidePick) => ({
			id: voterGuidePick.id,
			voterGuide: voterGuidePick.voterGuide,
		}));
	}, [enrichedData]);

	const totalRecommendations = useMemo(() => {
		return voterGuidePicks.length + endorsements.length;
	}, [voterGuidePicks, endorsements]);

	return {
		data: {
			voterGuidePicks,
			endorsements,
			totalRecommendations,
			headingText: `${totalRecommendations} Recommendation${totalRecommendations !== 1 ? 's' : ''}`,
		},
	};
};
